import { useEffect, useState, type ReactNode } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { cn } from "@/lib/utils";
import { formatEnrichmentMoney } from "@/lib/enrichmentMoney";
import {
  getEnrichmentStats,
  getEnrichmentTeamStats,
  listEnrichmentMembers,
  type EnrichmentTeamStatsResponse,
  type EnrichmentRates,
  type EnrichmentStatsPeriod,
  type EnrichmentStatsResponse,
} from "@/api/enrichment";

import type { EnrichmentMember } from "@/api/types";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { EnrichmentRecoveryPanel } from "./EnrichmentRecoveryPanel";

interface EnrichmentStatsProps {
  slug: string;
}

export function EnrichmentStats({ slug }: EnrichmentStatsProps) {
  // The pay period by default: an operator reading a rolling 30 days saw his
  // total fall while he worked, as its oldest day left the window.
  const [period, setPeriod] = useState<EnrichmentStatsPeriod>("this_month");
  const [filter, setFilter] = useState<{ slug: string; userId?: string }>({ slug });
  const userId = filter.slug === slug ? filter.userId : undefined;
  const requestKey = JSON.stringify([slug, period, userId]);
  const [result, setResult] = useState<{ key: string; stats: EnrichmentStatsResponse; team: EnrichmentTeamStatsResponse } | null>(null);
  const [errorKey, setErrorKey] = useState<string | null>(null);
  const [capability, setCapability] = useState<{ slug: string; team: boolean } | null>(null);
  const [members, setMembers] = useState<{ slug: string; items: EnrichmentMember[] } | null>(null);
  const [membersError, setMembersError] = useState(false);
  const [memberRetry, setMemberRetry] = useState(0);
  const canSelectOperator = capability?.slug === slug && capability.team;
  const currentMembers = members?.slug === slug ? members.items : [];
  const stats = result?.key === requestKey ? result.stats : null;
  const team = result?.key === requestKey ? result.team : null;
  const loaded = Boolean(stats) || errorKey === requestKey;

  useEffect(() => {
    let cancelled = false;
    setResult(null);
    setErrorKey(null);
    Promise.all([
      getEnrichmentStats(slug, period, userId),
      getEnrichmentTeamStats(slug, { period, ...(userId ? { user_id: userId } : {}) }),
    ]).then(([nextStats, nextTeam]) => {
      if (cancelled) return;
      if (!nextStats || nextStats.scope !== nextTeam.scope || nextStats.user_id !== nextTeam.user_id
        || (userId && nextStats.user_id !== userId)
        || (!userId && nextStats.scope === "team" && nextStats.user_id !== null)) {
        setErrorKey(requestKey);
        return;
      }
      setResult({ key: requestKey, stats: nextStats, team: nextTeam });
      setCapability({ slug, team: nextStats.scope === "team" });
    }).catch(() => {
      if (!cancelled) setErrorKey(requestKey);
    });
    return () => { cancelled = true; };
  }, [slug, period, userId, requestKey]);

  useEffect(() => {
    if (!canSelectOperator) return;
    let cancelled = false;
    setMembers(null);
    setMembersError(false);
    listEnrichmentMembers(slug).then(items => {
      if (!cancelled) setMembers({ slug, items });
    }).catch(() => {
      if (!cancelled) setMembersError(true);
    });
    return () => { cancelled = true; };
  }, [slug, canSelectOperator, memberRetry]);

  const totalEnriched = stats?.total_enriched ?? 0;
  const rates: EnrichmentRates = stats?.rates ?? { name: 0, linkedin: 0, phone: 0, email: 0 };
  const fullPerLead = rates.name + rates.linkedin + rates.phone + rates.email;
  const totalEarnings = stats?.total_earnings ?? 0;
  const teamScope = stats?.scope === "team" || canSelectOperator;
  const selectedName = currentMembers.find(member => member.user_id === userId)?.full_name
    || team?.operators.find(operator => operator.user_id === userId)?.full_name
    || "Selected operator";
  const individualView = stats?.scope === "self" || Boolean(userId);
  const money = (amount: number, maximumFractionDigits = 2) =>
    formatEnrichmentMoney(amount, stats?.currency, maximumFractionDigits);

  return (
    <div className="mx-auto max-w-6xl space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-lg font-semibold text-slate-900">
          {userId ? `${selectedName}’s Performance & Earnings` : teamScope ? "Team Performance & Earnings" : "My Performance & Earnings"}
        </h2>
        <div className="flex flex-wrap items-center gap-2">
          {canSelectOperator && <Select value={userId || "all"} onValueChange={value => setFilter({ slug, userId: value === "all" ? undefined : value })}>
            <SelectTrigger aria-label="Operator" className="w-44 bg-white"><SelectValue placeholder="Everyone" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Everyone</SelectItem>
              {currentMembers.map(member => <SelectItem key={member.user_id} value={member.user_id}>{member.full_name || member.email}</SelectItem>)}
            </SelectContent>
          </Select>}
          <PeriodTabs period={period} onChange={setPeriod} />
        </div>
      </div>

      {canSelectOperator && membersError && <div role="alert" className="flex items-center gap-2 text-sm text-amber-800">
        Operator list unavailable.
        <Button size="sm" variant="outline" onClick={() => setMemberRetry(value => value + 1)}>Retry operator list</Button>
      </div>}
      {!loaded && <div role="status" className="py-12 text-center text-sm text-slate-500">Loading statistics…</div>}

      {loaded && !stats && (
        <div className="surface-plane p-8 text-center">
          <p className="text-sm text-slate-600">
            Couldn&rsquo;t load enrichment statistics.
          </p>
          <p className="mt-1 text-xs text-slate-400">
            Try another period or refresh the page.
          </p>
        </div>
      )}

      {stats && (
        <>
          <div className="rounded-plane bg-gradient-to-r from-emerald-500 to-teal-500 p-6 text-white">
            <div className="text-sm opacity-80">
              {period === "this_month" ? "Estimated Earnings this month" : "Estimated Earnings"}
            </div>
            <div className="mt-1 text-4xl font-bold">
              {money(totalEarnings)}
            </div>
            <div className="mt-1 text-sm opacity-60">
              {teamScope && !userId ? (
                <>{totalEnriched} enrichments across the team, using each operator’s rate card</>
              ) : (
                <>{totalEnriched} enrichments, up to {money(fullPerLead, 6)}/lead when every field is found</>
              )}
            </div>
          </div>

          {team && <EnrichmentRecoveryPanel data={team} />}

          <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
            <Kpi value={totalEnriched} label="Completed enrichments" tone="slate" />
            <Kpi value={stats.total_corrections} label="Corrections" tone="amber" size="md" />
            <Kpi value={`${Math.round(stats.avg_time_seconds)}s`} label="Avg time per enrichment" tone="indigo" size="md" />
            <Kpi value={stats.streak} label="Active-day streak" tone="slate" size="md" />
          </div>

          <ChartCard title="Daily Enrichments">
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={stats.daily ?? []}>
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke="#f3f4f6"
                  vertical={false}
                />
                <XAxis
                  dataKey="date"
                  tick={{ fontSize: 10, fill: "#9ca3af" }}
                  tickFormatter={(d) =>
                    new Date(d).toLocaleDateString("en-US", {
                      timeZone: "UTC",
                      weekday: "short",
                      day: "numeric",
                    })
                  }
                />
                <YAxis
                  tick={{ fontSize: 10, fill: "#9ca3af" }}
                  axisLine={false}
                  tickLine={false}
                />
                <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} />
                <Bar
                  isAnimationActive={false}
                  dataKey="enriched"
                  fill="#6366f1"
                  radius={[4, 4, 0, 0]}
                  name="Enriched"
                />
                <Bar
                  isAnimationActive={false}
                  dataKey="corrections"
                  fill="#f59e0b"
                  radius={[4, 4, 0, 0]}
                  name="Corrections"
                />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>

          <ChartCard title="Earnings Over Time">
            <ResponsiveContainer width="100%" height={180}>
              <LineChart data={stats.daily ?? []}>
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke="#f3f4f6"
                  vertical={false}
                />
                <XAxis
                  dataKey="date"
                  tick={{ fontSize: 10, fill: "#9ca3af" }}
                  tickFormatter={(d) =>
                    new Date(d).toLocaleDateString("en-US", {
                      timeZone: "UTC",
                      day: "numeric",
                      month: "short",
                    })
                  }
                />
                <YAxis
                  tick={{ fontSize: 10, fill: "#9ca3af" }}
                  tickFormatter={(v: number) => money(v)}
                  axisLine={false}
                  tickLine={false}
                />
                <Tooltip
                  formatter={(v: number) => [money(v), "Earnings"]}
                  contentStyle={{ fontSize: 12, borderRadius: 8 }}
                />
                <Line
                  isAnimationActive={false}
                  type="monotone"
                  dataKey="earnings"
                  stroke="#10b981"
                  strokeWidth={2.5}
                  dot={{ r: 3, fill: "#10b981" }}
                />
              </LineChart>
            </ResponsiveContainer>
          </ChartCard>

          {individualView && stats.streak >= 3 && (
            <div className="flex items-center gap-3 rounded-xl border border-amber-200 bg-gradient-to-r from-amber-50 to-orange-50 p-4">
              <span className="text-3xl">🔥</span>
              <div>
                <span className="font-bold text-amber-800">
                  {stats.streak}-day streak!
                </span>
                <p className="text-xs text-amber-600">
                  Consistent work pays off, keep it up!
                </p>
              </div>
            </div>
          )}

          {individualView && <BadgeStrip stats={stats} fullContacts={team?.total.recovery?.with_both.count} />}

          {individualView && <div className="surface-plane p-4">
            <h3 className="mb-1 text-sm font-medium text-slate-700">
              {userId ? `${selectedName}’s Rate Card` : "Your Rate Card"}
            </h3>
            <p className="mb-3 text-xs text-slate-400">
              Per-field payout the backend uses to compute your earnings.
              Admins edit these in Settings → Enrichment Rates.
            </p>
            <div className="space-y-2 text-sm">
              <RateRow label="👤 Owner's name" rate={rates.name} currency={stats.currency} />
              <RateRow label="🔗 LinkedIn" rate={rates.linkedin} currency={stats.currency} />
              <RateRow label="📞 Phone" rate={rates.phone} currency={stats.currency} />
              <RateRow label="✉️ Email" rate={rates.email} currency={stats.currency} />
              <div className="flex items-center justify-between border-t border-slate-900/[0.06] pt-2 text-xs text-slate-500">
                <span>Full enrichment (all fields)</span>
                <span className="font-mono">{money(fullPerLead, 6)}</span>
              </div>
              <div className="flex items-center justify-between py-2 font-bold">
                <span>Earned this period</span>
                <span className="font-mono text-emerald-600">
                  {money(totalEarnings)}
                </span>
              </div>
            </div>
          </div>}
        </>
      )}
    </div>
  );
}

function PeriodTabs({
  period,
  onChange,
}: {
  period: EnrichmentStatsPeriod;
  onChange: (next: EnrichmentStatsPeriod) => void;
}) {
  const options: Array<{ value: EnrichmentStatsPeriod; label: string }> = [
    { value: "this_month", label: "This month" },
    { value: "week", label: "Last 7 days" },
    { value: "month", label: "Last 30 days" },
    { value: "all", label: "All Time" },
  ];
  return (
    <div className="flex items-center gap-1 rounded-lg bg-slate-100 p-0.5">
      {options.map((p) => (
        <button
          key={p.value}
          type="button"
          onClick={() => onChange(p.value)}
          className={cn(
            "rounded-md px-3 py-1.5 text-xs font-medium transition-all",
            period === p.value
              ? "bg-white text-slate-900 shadow-sm-tile"
              : "text-slate-500 hover:text-slate-700",
          )}
        >
          {p.label}
        </button>
      ))}
    </div>
  );
}

const KPI_TONES: Record<string, { value: string; label: string }> = {
  slate: { value: "text-slate-900", label: "text-slate-500" },
  emerald: { value: "text-emerald-600", label: "text-emerald-500" },
  amber: { value: "text-amber-600", label: "text-amber-500" },
  indigo: { value: "text-indigo-600", label: "text-slate-500" },
  muted: { value: "text-slate-400", label: "text-slate-400" },
};

function Kpi({
  value,
  label,
  tone,
  size = "lg",
}: {
  value: number | string;
  label: string;
  tone: keyof typeof KPI_TONES;
  size?: "md" | "lg";
}) {
  const styles = KPI_TONES[tone];
  return (
    <div className="surface-plane p-4 text-center">
      <div
        className={cn(
          size === "lg" ? "text-3xl" : "text-2xl",
          "font-bold",
          styles.value,
        )}
      >
        {value}
      </div>
      <div className={cn("mt-1 text-xs", styles.label)}>{label}</div>
    </div>
  );
}

function ChartCard({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) {
  return (
    <div className="surface-plane p-4">
      <h3 className="mb-3 text-sm font-medium text-slate-700">{title}</h3>
      {children}
    </div>
  );
}

function RateRow({ label, rate, currency }: { label: string; rate: number; currency: string }) {
  return (
    <div className="flex items-center justify-between border-b border-slate-50 py-1.5">
      <span className="text-slate-600">{label}</span>
      <span className="font-mono font-medium">
        {formatEnrichmentMoney(rate, currency, 6)}
        <span className="ml-1 text-[10px] text-slate-400">per lead</span>
      </span>
    </div>
  );
}

function BadgeStrip({ stats, fullContacts }: { stats: EnrichmentStatsResponse; fullContacts?: number }) {
  const badges: Array<{ key: string; label: string; tone: string }> = [];
  if (stats.total_enriched >= 10) {
    badges.push({
      key: "10",
      label: "🚀 10+ enrichments",
      tone: "bg-indigo-50 text-indigo-700",
    });
  }
  if (stats.total_enriched >= 50) {
    badges.push({
      key: "50",
      label: "💪 50+ enrichments",
      tone: "bg-violet-50 text-violet-700",
    });
  }
  if (stats.total_enriched >= 100) {
    badges.push({
      key: "100",
      label: "🏆 100+ enrichments",
      tone: "bg-amber-50 text-amber-700",
    });
  }
  if (fullContacts !== undefined && fullContacts >= 50) {
    badges.push({
      key: "full50",
      label: "🎯 50+ full contacts",
      tone: "bg-emerald-50 text-emerald-700",
    });
  }
  if (stats.avg_time_seconds > 0 && stats.avg_time_seconds < 60) {
    badges.push({
      key: "speed",
      label: "⚡ Speed demon (< 1min avg)",
      tone: "bg-cyan-50 text-cyan-700",
    });
  }
  if (stats.streak >= 5) {
    badges.push({
      key: "streak",
      label: `🔥 ${stats.streak}-day streak`,
      tone: "bg-orange-50 text-orange-700",
    });
  }
  if (badges.length === 0) return null;
  return (
    <div className="flex flex-wrap gap-2">
      {badges.map((b) => (
        <span
          key={b.key}
          className={cn(
            "rounded-full px-3 py-1.5 text-xs font-medium",
            b.tone,
          )}
        >
          {b.label}
        </span>
      ))}
    </div>
  );
}
